import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import L from 'leaflet';
import * as esri from 'esri-leaflet';
import 'leaflet/dist/leaflet.css';
import './customPopup.css';

import Header from "./Header";
import WmsLayerComponent from "./wmsLayer";
import LayerManager from "./LayerManager";
import JumpToCoordinates from "./JumpToCoordinates";
import CustomZoomControl from "./CustomZoomControl";
import AnalysisSidebar from "./AnalysisSidebar"; 
import ParcelSearch from "./ParcelSearch";
import api_service from "../../api/api_service";

const EDITABLE_LAYERS_MAP = {
    'PI': 'Plasticity Index',
    'GSI_final1': 'Geological Strength Index (GSI)',
    'clay1': 'Clay Content (%)',
    'Lira_gew1': 'Lithology'
};

const DROPDOWN_OPTIONS = {
    'PI': [
        { value: '', label: 'N/A' },
        { value: '10', label: '<20%' },
        { value: '30', label: '20–40%' },
        { value: '50', label: '40–60%' },
        { value: '70', label: '>60%' }
    ],
    'clay1': [
        { value: '', label: 'N/A' },
        { value: '7.5', label: '<15%' },
        { value: '22.5', label: '15–30%' },
        { value: '37.5', label: '30–45%' },
        { value: '60', label: '>45%' }
    ],
    'GSI_final1': [
        { value: '', label: 'N/A' },
        { value: '20', label: '0-40' },
        { value: '50', label: '40-60' },
        { value: '75', label: '60-90' }
    ],
    'Lira_gew1': [
        { value: '', label: 'N/A' },
        { value: '1', label: 'Quaternary formations' },
        { value: '2', label: 'Nicosia Formation' },
        { value: '3', label: 'Evaporites' },
        { value: '4', label: 'Pachna Formation' },
        { value: '5', label: 'Lefkara Formation' },
        { value: '6', label: 'Talus' },
        { value: '7', label: 'Kannaviou Formation' },
        { value: '8', label: 'Agia Varvara Formation' },
        { value: '9', label: 'Pera pedi' },
        { value: '10', label: 'Melange' },
        { value: '11', label: 'Mesozoic fine grained clastics' },
        { value: '12', label: 'Limestones' },
        { value: '13', label: 'Silica sandstones' },
        { value: '14', label: 'Lavas' },
        { value: '15', label: 'Subvolcanic' },
        { value: '16', label: 'Serpentinite' },
        { value: '17', label: 'Plutonic' },
        { value: '18', label: 'Flysch' }
    ]
};

const districtMapping = {
    1: "NICOSIA", 2: "KYRENIA", 3: "FAMAGUSTA", 
    4: "LARNACA", 5: "LIMASSOL", 6: "PAPHOS"
};

const MapComponent = () => {
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [activeQueryableLayers, setActiveQueryableLayers] = useState([]);
    const [resultLayers, setResultLayers] = useState([]);
    const [featureInfo, setFeatureInfo] = useState(null);
    const [featureInfoPosition, setFeatureInfoPosition] = useState(null);
    const [analyses, setAnalyses] = useState([]); 
    const popupRef = useRef(null);
    const layerManagerRef = useRef(null);
    const [showCadastral, setShowCadastral] = useState(false);
    const [showOrthophoto, setShowOrthophoto] = useState(false);
    const [showParcelSearch, setShowParcelSearch] = useState(false);
    const [isBboxPresent, setIsBboxPresent] = useState(false);
    
    const bboxRef = useRef({ layer: null, cleanup: null });
    const basemapLayerRef = useRef(null);
    const cadastralLayerRef = useRef(null);
    const orthophotoLayerRef = useRef(null);
    const initialValuesRef = useRef({});

    const refreshAnalyses = useCallback(async () => {
        try {
            const data = await api_service.fetchPixelAnalyses();
            setAnalyses(data);
        } catch (error) {
            console.error("Failed to fetch analyses:", error);
        }
    }, []);

    useEffect(() => {
        refreshAnalyses();
        const interval = setInterval(refreshAnalyses, 10000);
        return () => clearInterval(interval);
    }, [refreshAnalyses]);

    const queryLayerAtPoint = useCallback(async (latlng) => {
        if (popupRef.current && map) {
            map.closePopup(popupRef.current);
            popupRef.current = null;
        }
        
        const layerToQuery = resultLayers.length > 0 
            ? resultLayers[resultLayers.length - 1] 
            : activeQueryableLayers[0];

        if (!map || !layerToQuery) return;

        const { lat, lng } = latlng;
        const mapContext = {
            bbox: map.getBounds().toBBoxString(),
            width: map.getSize().x,
            height: map.getSize().y,
            x: Math.round(map.latLngToContainerPoint(latlng).x),
            y: Math.round(map.latLngToContainerPoint(latlng).y)
        };

        try {
            const results = await api_service.getLayerValueByCoords(layerToQuery.layer_name, lng, lat, mapContext);

            if (results && results.length > 0) {
                let content = '<div style="display: flex; flex-direction: column; max-height: 400px; min-width: 280px;">';
                content += '<div style="flex: 1; overflow-y: auto; padding-right: 5px; margin-bottom: 12px;">';
                
                const isSuscMap = results.some(r => r.layer === 'susc_map');
                const editableKeys = Object.keys(EDITABLE_LAYERS_MAP);
                const currentInitials = {};

                const sortedResults = [...results].sort((a, b) => {
                    if (a.layer === 'susc_map') return -1;
                    if (b.layer === 'susc_map') return 1;
                    const aEditable = editableKeys.includes(a.layer) ? 0 : 1;
                    const bEditable = editableKeys.includes(b.layer) ? 0 : 1;
                    return aEditable - bEditable;
                });

                sortedResults.forEach((result) => {
                    const displayName = result.fe_layer_name || result.layer;
                    const rawVal = result.value;
                    const displayVal = rawVal === null || rawVal === undefined ? "N/A" : (typeof rawVal === 'number' ? rawVal.toFixed(4) : rawVal);

                    if (editableKeys.includes(result.layer)) {
                        currentInitials[result.layer] = rawVal?.toString() || "";
                    }

                    if (result.layer === 'susc_map') {
                        content += `
                            <div style="padding: 12px; background: #2c3e50; color: white; border-radius: 6px; margin-bottom: 10px; border-left: 5px solid #e67e22;">
                                <div style="font-size: 0.7em; text-transform: uppercase; opacity: 0.7;">Main Assessment</div>
                                <div style="font-weight: bold; font-size: 1em;">${displayName}</div>
                                <div style="font-size: 1.1em; margin-top: 4px;">Value: ${displayVal}</div>
                            </div>
                            <div style="font-size: 0.75em; font-weight: bold; color: #7f8c8d; margin-bottom: 8px;">PARAMETERS:</div>
                        `;
                    } else if (isSuscMap && editableKeys.includes(result.layer)) {
                        // FIX: Ensure original value is displayed
                        const originalLabel = rawVal === null || rawVal === undefined ? "N/A" : rawVal;
                        
                        if (DROPDOWN_OPTIONS[result.layer]) {
                            const options = DROPDOWN_OPTIONS[result.layer];
                            let optionsHtml = '';
                            options.forEach(opt => {
                                const selected = rawVal?.toString() === opt.value ? 'selected' : '';
                                optionsHtml += `<option value="${opt.value}" ${selected}>${opt.label}</option>`;
                            });
                            content += `
                                <div style="padding: 10px; margin-bottom: 8px; border-radius: 6px; background: #fff; border: 1.5px solid #e67e22;">
                                    <div style="font-weight: 700; font-size: 0.85em; color: #2c3e50;">${displayName}</div>
                                    <div style="font-size: 0.8em; color: #7f8c8d; margin-top: 2px;">Original: ${originalLabel}</div>
                                    <div style="margin-top: 6px;">
                                        <select class="dep-param-input" data-layer="${result.layer}" style="width: 100%; padding: 6px 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 0.9em; box-sizing: border-box; outline: none; cursor: pointer;">
                                            ${optionsHtml}
                                        </select>
                                    </div>
                                </div>
                            `;
                        } else {
                            const inputType = typeof rawVal === 'number' || rawVal === null ? 'number' : 'text';
                            content += `
                                <div style="padding: 10px; margin-bottom: 8px; border-radius: 6px; background: #fff; border: 1.5px solid #e67e22;">
                                    <div style="font-weight: 700; font-size: 0.85em; color: #2c3e50;">${displayName}</div>
                                    <div style="font-size: 0.8em; color: #7f8c8d; margin-top: 2px;">Original: ${originalLabel}</div>
                                    <div style="margin-top: 6px;">
                                        <input type="${inputType}" step="0.01" class="dep-param-input" data-layer="${result.layer}" value="${rawVal !== null ? rawVal : ''}" style="width: 100%; padding: 6px 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 0.9em; box-sizing: border-box; outline: none;"/>
                                    </div>
                                </div>
                            `;
                        }
                    } else {
                        content += `
                            <div style="padding: 10px; margin-bottom: 6px; border-radius: 6px; background: #f1f3f5; border: 1px solid #dee2e6;">
                                <div style="font-weight: bold; font-size: 0.8em; color: #6c757d;">${displayName}</div>
                                <div style="font-size: 0.95em; color: #495057; font-weight: 500; margin-top: 2px;">Value: ${displayVal}</div>
                            </div>
                        `;
                    }
                });
                
                initialValuesRef.current = currentInitials;
                content += '</div>';

                if (isSuscMap) {
                    content += `
                        <div style="border-top: 2px solid #eee; padding-top: 10px;">
                            <button id="submit-analysis-global-btn" disabled data-lat="${lat}" data-lng="${lng}" 
                                style="width: 100%; background: #bdc3c7; color: white; border: none; padding: 12px; border-radius: 5px; cursor: not-allowed; font-weight: bold; font-size: 0.9em;">
                                MODIFY VALUES TO ANALYZE
                            </button>
                        </div>`;
                }
                content += '</div>';
                setFeatureInfo({ html: content });
            } else {
                setFeatureInfo({ html: "No data available." });
            }
            setFeatureInfoPosition(latlng);
        } catch (error) {
            console.error('Query Error:', error);
            setFeatureInfo({ error: 'Error fetching values.' });
            setFeatureInfoPosition(latlng);
        }
    }, [map, activeQueryableLayers, resultLayers]);

    const handleViewAnalysis = (analysis) => {
        const fileName = analysis.filepath.split('/').pop();
        const layerName = fileName.replace('.tif', '');
        const isShowing = resultLayers.some(l => l.id === analysis.id);

        if (isShowing) {
            setResultLayers(prev => prev.filter(l => l.id !== analysis.id));
            if (map) map.closePopup();
        } else {
            const newLayer = {
                id: analysis.id,
                url: `/geoserver/lira/wms`,
                wmsParams: { 
                    layers: `lira:${layerName}`, format: 'image/png', transparent: true, version: '1.1.0', tiled: true 
                },
                zIndex: 1500,
                layer_name: layerName,
                fe_layer_name: `Analysis: ${analysis.latitude.toFixed(3)}, ${analysis.longitude.toFixed(3)}`
            };
            setResultLayers(prev => [...prev, newLayer]);
            if (map) {
                map.flyTo([analysis.latitude, analysis.longitude], 18);
                setTimeout(() => {
                    queryLayerAtPoint({ lat: analysis.latitude, lng: analysis.longitude });
                }, 800); 
            }
        }
    };

    const handleExportClick = useCallback(() => {
        if (!map) return;
        if (bboxRef.current.layer) {
            map.removeLayer(bboxRef.current.layer);
            if (bboxRef.current.cleanup) bboxRef.current.cleanup();
        }

        const center = map.getCenter();
        const offset = 0.010;
        const bounds = [[center.lat - offset, center.lng - offset], [center.lat + offset, center.lng + offset]];
        
        const rectangle = L.rectangle(bounds, { 
            color: "blue", 
            weight: 3, 
            fillOpacity: 0.2, 
            dashArray: '5, 5',
            interactive: true,
            bubblingMouseEvents: false
        }).addTo(map);

        let isDragging = false;
        let startPoint;
        let startBounds;

        const onMouseDown = (e) => {
            isDragging = true;
            startPoint = e.latlng;
            startBounds = rectangle.getBounds();
            map.dragging.disable();
            L.DomEvent.stopPropagation(e);
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            const currentPoint = e.latlng;
            const latDiff = currentPoint.lat - startPoint.lat;
            const lngDiff = currentPoint.lng - startPoint.lng;
            const newBounds = [
                [startBounds.getSouth() + latDiff, startBounds.getWest() + lngDiff],
                [startBounds.getNorth() + latDiff, startBounds.getEast() + lngDiff]
            ];
            rectangle.setBounds(newBounds);
        };

        const onMouseUp = () => {
            if (isDragging) {
                isDragging = false;
                map.dragging.enable();
            }
        };

        rectangle.on('mousedown', onMouseDown);
        map.on('mousemove', onMouseMove);
        map.on('mouseup', onMouseUp);

        bboxRef.current = { 
            layer: rectangle, 
            cleanup: () => {
                rectangle.off('mousedown', onMouseDown);
                map.off('mousemove', onMouseMove);
                map.off('mouseup', onMouseUp);
            } 
        };
        setIsBboxPresent(true);
    }, [map]);

    const handleClearBbox = useCallback(() => {
        if (bboxRef.current.layer) {
            map.removeLayer(bboxRef.current.layer);
            if (bboxRef.current.cleanup) bboxRef.current.cleanup();
            bboxRef.current = { layer: null, cleanup: null };
        }
        setIsBboxPresent(false);
    }, [map]);

    const handleDownloadClick = useCallback(async () => {
        if (!bboxRef.current.layer) return;
        const bounds = bboxRef.current.layer.getBounds();
        const bboxArray = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
        const activeLayer = resultLayers.length > 0 ? resultLayers[resultLayers.length - 1] : activeQueryableLayers[0];
        if (!activeLayer) return alert("Select a layer first.");
        
        try {
            const res = await api_service.getPixelValues(activeLayer.layer_name, bboxArray);
            const { data, width, height, bbox: resBbox } = res;
            const [minX, minY, maxX, maxY] = resBbox;
            const cellWidth = (maxX - minX) / width;
            const cellHeight = (maxY - minY) / height;
            
            const features = data.map((val, index) => {
                const row = Math.floor(index / width);
                const col = index % width;
                const lon = minX + (col * cellWidth) + (cellWidth / 2);
                const lat = maxY - (row * cellHeight) - (cellHeight / 2);
                return { type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties: { value: val } };
            });
            
            const geojson = { type: "FeatureCollection", features };
            const blob = new Blob([JSON.stringify(geojson)], { type: "application/geo+json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `export_${activeLayer.layer_name}.geojson`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) { alert("Failed to export GeoJSON."); }
    }, [activeQueryableLayers, resultLayers]);

    useEffect(() => {
        const handleInteraction = async (e) => {
            if (e.target && e.target.classList.contains('dep-param-input')) {
                const submitBtn = document.getElementById('submit-analysis-global-btn');
                const inputs = document.querySelectorAll('.dep-param-input');
                let hasChanged = false;
                inputs.forEach(input => {
                    const layer = input.getAttribute('data-layer');
                    const initialVal = initialValuesRef.current[layer];
                    if (input.value.toString() !== initialVal?.toString()) hasChanged = true;
                });

                if (submitBtn) {
                    if (hasChanged) {
                        submitBtn.disabled = false;
                        submitBtn.style.background = "#e67e22";
                        submitBtn.style.cursor = "pointer";
                        submitBtn.innerText = "SUBMIT FOR ANALYSIS";
                    } else {
                        submitBtn.disabled = true;
                        submitBtn.style.background = "#bdc3c7";
                        submitBtn.style.cursor = "not-allowed";
                        submitBtn.innerText = "MODIFY VALUES TO ANALYZE";
                    }
                }
            }

            if (e.target && e.target.id === 'submit-analysis-global-btn') {
                if (e.target.disabled) return;
                const lat = parseFloat(e.target.getAttribute('data-lat'));
                const lng = parseFloat(e.target.getAttribute('data-lng'));
                const overrides = {};
                document.querySelectorAll('.dep-param-input').forEach(input => {
                    overrides[input.getAttribute('data-layer')] = input.value;
                });
                e.target.innerText = "PROCESSING...";
                e.target.disabled = true;
                try {
                    await api_service.submitPixelAnalysis({ layer_name: "susc_map", lon: lng, lat: lat, overrides });
                    alert("Analysis request submitted successfully.");
                    if (map) map.closePopup();
                    refreshAnalyses();
                } catch (err) {
                    alert(`Submission failed: ${err.message}`);
                    e.target.innerText = "SUBMIT FOR ANALYSIS";
                    e.target.disabled = false;
                }
            }
        };

        document.addEventListener('input', handleInteraction);
        document.addEventListener('change', handleInteraction);
        document.addEventListener('click', handleInteraction);
        return () => {
            document.removeEventListener('input', handleInteraction);
            document.removeEventListener('change', handleInteraction);
            document.removeEventListener('click', handleInteraction);
        }
    }, [map, refreshAnalyses]);

    useEffect(() => {
        if (!map) return;
        if (!basemapLayerRef.current) {
            basemapLayerRef.current = esri.basemapLayer('Streets');
            basemapLayerRef.current.addTo(map);
        }
        if (!cadastralLayerRef.current) {
            cadastralLayerRef.current = esri.featureLayer({
                url: 'https://eservices.dls.moi.gov.cy/arcgis/rest/services/National/CadastralMap_EN/MapServer/0',
                style: { color: '#00b38f', weight: 1.5, fillOpacity: 0.1 }
            });
            cadastralLayerRef.current.on('click', function(e) {
                const props = e.layer.feature.properties;
                const popupContent = `
                    <div style="font-family: Arial, sans-serif; padding: 10px; min-width: 250px;">
                        <h3 style="margin: 0 0 10px 0; color: #2193b0; font-size: 16px; border-bottom: 2px solid #2193b0; padding-bottom: 5px;">Parcel Info</h3>
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr><td style="font-weight:bold;">District</td><td>${districtMapping[props.DIST_CODE] || props.DIST_CODE || ""}</td></tr>
                            <tr><td style="font-weight:bold;">Sheet</td><td>${props.SHEET || ""}</td></tr>
                            <tr><td style="font-weight:bold;">Plan</td><td>${props.PLAN_NBR || ""}</td></tr>
                            <tr><td style="font-weight:bold;">Block</td><td>${props.BLCK_CODE || ""}</td></tr>
                            <tr><td style="font-weight:bold;">Parcel</td><td>${props.PARCEL_NBR || props.PARCEL || ""}</td></tr>
                        </table>
                    </div>`;
                L.popup().setLatLng(e.latlng).setContent(popupContent).openOn(map);
            });
        }
        if (!orthophotoLayerRef.current) {
            orthophotoLayerRef.current = esri.tiledMapLayer({
                url: 'https://eservices.dls.moi.gov.cy/arcgis/rest/services/BASEMAPS/Orthoimagery_2019_WebMercator/MapServer'
            });
        }
    }, [map]);

    useEffect(() => {
        if (!map || !cadastralLayerRef.current) return;
        if (showCadastral) { if (!map.hasLayer(cadastralLayerRef.current)) cadastralLayerRef.current.addTo(map); }
        else { if (map.hasLayer(cadastralLayerRef.current)) map.removeLayer(cadastralLayerRef.current); }
    }, [map, showCadastral]);

    useEffect(() => {
        if (!map || !orthophotoLayerRef.current) return;
        if (showOrthophoto) { if (!map.hasLayer(orthophotoLayerRef.current)) orthophotoLayerRef.current.addTo(map); }
        else { if (map.hasLayer(orthophotoLayerRef.current)) map.removeLayer(orthophotoLayerRef.current); }
    }, [map, showOrthophoto]);

    useEffect(() => {
        if (!map) return;
        const onClick = (e) => queryLayerAtPoint(e.latlng);
        map.on('click', onClick);
        return () => map.off('click', onClick);
    }, [map, queryLayerAtPoint]);

    useEffect(() => {
        if (!map || !featureInfo || !featureInfoPosition) return;
        popupRef.current = L.popup({ minWidth: 280, className: 'modern-analysis-popup' })
            .setLatLng(featureInfoPosition)
            .setContent(`<div class="analysis-popup-container">${featureInfo.html || featureInfo.error}</div>`)
            .openOn(map);
    }, [featureInfo, featureInfoPosition, map]);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <Header />
            <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
                <AnalysisSidebar 
                    analyses={analyses}
                    onViewAnalysis={handleViewAnalysis}
                    activeResultIds={resultLayers.map(l => l.id)}
                    showCadastral={showCadastral}
                    onToggleCadastral={() => setShowCadastral(!showCadastral)}
                    showOrthophoto={showOrthophoto}
                    onToggleOrthophoto={() => setShowOrthophoto(!showOrthophoto)}
                    showParcelSearch={showParcelSearch}
                    onToggleParcelSearch={() => setShowParcelSearch(!showParcelSearch)}
                    onExportClick={handleExportClick}
                    onClearBbox={handleClearBbox}
                    onDownloadClick={handleDownloadClick}
                    isBboxPresent={isBboxPresent}
                />
                {showParcelSearch && map && cadastralLayerRef.current && (
                    <ParcelSearch 
                        map={map}
                        cadastralLayer={cadastralLayerRef.current}
                        onClose={() => setShowParcelSearch(false)}
                    />
                )}
                <MapContainer 
                    center={[34.916, 33.625]} 
                    zoom={9} 
                    ref={setMap} 
                    style={{ height: "100%" }}
                    whenReady={(mapInstance) => setMap(mapInstance.target)}
                >
                    <LayerManager ref={layerManagerRef} onActiveQueryableLayersChange={setActiveQueryableLayers} />
                    {resultLayers.map(l => (
                        <WmsLayerComponent key={l.id} url={l.url} options={l.wmsParams} zIndex={l.zIndex} />
                    ))}
                    <CustomZoomControl />
                    <JumpToCoordinates onJump={queryLayerAtPoint} />
                </MapContainer>
            </div>
        </div>
    );
};

export default MapComponent;